"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.DATABASE_SHARD_DIRECTORY_FIELD = void 0;
exports.storedDatabaseShardDirectory = storedDatabaseShardDirectory;
exports.databaseShardDirectoryPartSource = databaseShardDirectoryPartSource;
exports.readDatabaseShardDirectoryValueHead = readDatabaseShardDirectoryValueHead;
exports.readDatabaseShardDirectoryState = readDatabaseShardDirectoryState;
exports.readDatabaseShardDirectory = readDatabaseShardDirectory;
exports.readDatabaseShardDirectoryHead = readDatabaseShardDirectoryHead;
exports.readDatabaseShardDirectoryPartDocIds = readDatabaseShardDirectoryPartDocIds;
exports.readDatabaseShardDirectoryShardPartDocIds = readDatabaseShardDirectoryShardPartDocIds;
exports.readDatabaseShardDirectoryBlockHead = readDatabaseShardDirectoryBlockHead;
exports.readDatabaseShardDirectoryBlockPartDocIds = readDatabaseShardDirectoryBlockPartDocIds;
exports.readDatabaseShardDirectoryBlock = readDatabaseShardDirectoryBlock;
exports.readDatabaseShardDirectoryBlockState = readDatabaseShardDirectoryBlockState;
exports.readDatabaseShardDirectoryBlockParts = readDatabaseShardDirectoryBlockParts;
exports.loadDatabaseShardDirectoryBlock = loadDatabaseShardDirectoryBlock;
exports.readDatabaseShardDirectoryWindow = readDatabaseShardDirectoryWindow;
exports.readDatabaseShardDirectoryLoaded = readDatabaseShardDirectoryLoaded;
exports.readDatabaseShardDirectoryDocIds = readDatabaseShardDirectoryDocIds;
exports.readDatabaseShardDirectoryProps = readDatabaseShardDirectoryProps;
exports.hasDatabaseShardDirectory = hasDatabaseShardDirectory;
exports.snapshotPropsHaveDatabaseShardDirectory = snapshotPropsHaveDatabaseShardDirectory;
exports.withoutDatabaseShardDirectory = withoutDatabaseShardDirectory;
exports.trackDatabaseShardDirectory = trackDatabaseShardDirectory;
exports.isDatabaseShardDirectoryProp = isDatabaseShardDirectoryProp;
exports.databaseShardDirectorySnapshotProps = databaseShardDirectorySnapshotProps;
exports.readDatabaseShardDirectoryBlockSnapshot = readDatabaseShardDirectoryBlockSnapshot;
exports.blockHasDatabaseShardDirectory = blockHasDatabaseShardDirectory;
exports.databaseShardDirectoryMap = databaseShardDirectoryMap;
exports.deleteDatabaseShardDirectory = deleteDatabaseShardDirectory;
exports.databaseShardDirectoryPath = databaseShardDirectoryPath;
const database_bytes_text_1 = require("./database-bytes-text");
const database_manifest_fields_1 = require("./database-manifest-fields");
const database_shard_directory_parts_1 = require("./database-shard-directory-parts");
const database_shard_manifest_crdt_1 = require("./database-shard-manifest-crdt");
const database_storage_errors_1 = require("./database-storage-errors");
const index_1 = require("./index");
/**
 * The row-shard directory of a database block: its shard list with the rows,
 * value summary, fingerprints and status of every shard. Code outside this
 * package reads and stores it only through these functions and the CRDT
 * readers and writers of `database-shard-manifest-crdt`, so it never depends
 * on where or in which form the directory is stored.
 *
 * The directory is the `databaseShardManifest` prop of the database block
 * (`prop:databaseShardManifest` in the block map). A block stores its head;
 * the shard entries live in directory part documents of the workspace (see
 * `database-shard-directory-parts`). That is the only stored form: a stored
 * list or another storage version throws a named error. A JSON snapshot
 * carries the complete list as its transport form, read only by
 * {@link readDatabaseShardDirectoryBlockSnapshot}. Readers of a stored block
 * pick the narrowest read they need:
 * - {@link readDatabaseShardDirectoryHead}: database fields and row counts,
 *   never waits for a part;
 * - {@link readDatabaseShardDirectoryWindow}: the shards of one row window;
 * - {@link readDatabaseShardDirectoryState}: the complete list, or the part
 *   documents still to load;
 * - {@link readDatabaseShardDirectory}: the complete list, which throws
 *   {@link DatabaseShardDirectoryPartsMissingError} while parts are missing.
 */
const DIRECTORY_PROP_KEY = 'databaseShardManifest';
/**
 * Whether a block or snapshot stores a directory: none when the value is
 * absent. A present value that is no map is malformed and never read as a
 * missing directory, which a writer would rebuild.
 */
function storesDirectory(stored) {
    if (stored === undefined)
        return false;
    if (stored === null || typeof stored !== 'object')
        throw new database_storage_errors_1.DatabaseShardDirectoryFormatError('Database shard directory must be stored as a map');
    return true;
}
function propsOf(host) {
    return host?.props;
}
/**
 * The directory as stored in block props, without reading it. Only for byte
 * budgets of the stored props and for identity records that hand it on to a
 * later reader; everything else reads it with
 * {@link readDatabaseShardDirectory}.
 */
function storedDatabaseShardDirectory(props) {
    return props && typeof props === 'object'
        ? props[DIRECTORY_PROP_KEY]
        : undefined;
}
/** Directory parts as the workspace documents of `docs` hold them. */
function databaseShardDirectoryPartSource(docs) {
    return visitedPartSource(docs, undefined);
}
function visitedPartSource(docs, 
/** Every part document read: the doc, or `undefined` while absent. */
visit) {
    if (!docs)
        return undefined;
    return partDocId => {
        const doc = docs.getDoc(partDocId)?.spaceDoc;
        visit?.(partDocId, doc);
        return doc && (0, database_shard_directory_parts_1.databaseShardDirectoryPartMap)(doc);
    };
}
/** Readers share one read head, so none of them may change it. */
function freezeHead(head) {
    if (!head)
        return undefined;
    for (const part of head.parts)
        Object.freeze(part);
    Object.freeze(head.parts);
    return Object.freeze(head);
}
/** Readers share one read directory, so none of them may change it. */
function freezeDirectory(directory) {
    for (const shard of directory.shards) {
        // The fingerprints stay byte arrays; only their record is frozen.
        if (shard.rowFingerprints)
            Object.freeze(shard.rowFingerprints);
        if (shard.valueKeys)
            Object.freeze(shard.valueKeys);
        Object.freeze(shard);
    }
    Object.freeze(directory.shards);
    return Object.freeze(directory);
}
function partSourceOf(host) {
    return databaseShardDirectoryPartSource(host?.store?.workspace);
}
function readStoredDirectory(stored, source) {
    const head = headOfStored(stored);
    if (!head)
        return undefined;
    const read = (0, database_shard_directory_parts_1.assembleDatabaseShardDirectory)(head, source);
    return read.status === 'ready'
        ? { status: 'ready', manifest: freezeDirectory(read.manifest) }
        : read;
}
/**
 * The stored head, undefined without a directory. A stored list, another
 * storage version or a broken value throws its named error.
 */
function headOfStored(stored) {
    if (!storesDirectory(stored))
        return undefined;
    return (0, database_shard_directory_parts_1.normalizeDatabaseShardDirectoryHead)(stored);
}
/**
 * The head of a directory value as a block stores it, for a reader that holds
 * the stored value itself (a root block read as JSON). Undefined without a
 * directory; a present value that is no head throws its named error.
 */
function readDatabaseShardDirectoryValueHead(stored) {
    return headOfStored(stored);
}
function partDocIdsOfStored(stored) {
    const head = headOfStored(stored);
    return head ? (0, database_shard_directory_parts_1.databaseShardDirectoryPartDocIds)(head) : [];
}
function completeDirectory(stored, read) {
    if (!read)
        return undefined;
    if (read.status === 'ready')
        return read.manifest;
    throw new database_storage_errors_1.DatabaseShardDirectoryPartsMissingError((0, database_manifest_fields_1.readStringField)(stored, 'databaseId'), read.partDocIds);
}
/**
 * Complete directory of a database block model or snapshot, if the block has
 * one, or the part documents still to load.
 */
function readDatabaseShardDirectoryState(host) {
    return readStoredDirectory(storedDatabaseShardDirectory(propsOf(host)), partSourceOf(host));
}
/** Transactions a doc started so far, counted from its first directory read. */
const transactionEpochs = new WeakMap();
function transactionEpoch(doc) {
    const known = transactionEpochs.get(doc);
    if (known)
        return known.value;
    const epoch = { value: 0 };
    doc.on('beforeTransaction', () => {
        epoch.value += 1;
    });
    transactionEpochs.set(doc, epoch);
    return epoch.value;
}
function isChanging(doc) {
    return doc._transactionCleanups.length > 0;
}
function isCurrentRead(read, epoch, docs) {
    return (read.epoch === epoch &&
        read.parts.every(part => {
            const doc = docs?.getDoc(part.partDocId)?.spaceDoc;
            return (doc === part.doc &&
                !isChanging(doc) &&
                transactionEpoch(doc) === part.epoch);
        }) &&
        read.absentPartDocIds.every(partDocId => !docs?.getDoc(partDocId)?.spaceDoc));
}
/**
 * `read` of the stored directory of `host`. A live model keeps the value in
 * `cache`, by the stored directory value of its block, until its doc or one of
 * the part documents the read visited starts the next transaction. Every
 * change of the directory, local or remote, a shard split or a compaction, is
 * such a transaction; while one runs, and for a snapshot, it reads afresh. A
 * part document the read found absent ends the value once the workspace holds
 * it; its first content arrives in a transaction of its own. A value `keep`
 * refuses is never kept.
 */
function readCachedDirectory(cache, host, read, keep) {
    const stored = storedDatabaseShardDirectory(propsOf(host));
    const doc = host?.yBlock?.doc;
    if (!host?.yBlock ||
        !doc ||
        !stored ||
        typeof stored !== 'object' ||
        isChanging(doc)) {
        return read(stored, partSourceOf(host));
    }
    const docs = host.store?.workspace;
    const epoch = transactionEpoch(doc);
    const cached = cache.get(stored);
    if (cached && isCurrentRead(cached, epoch, docs))
        return cached.value;
    const parts = [];
    const absentPartDocIds = [];
    let partChanging = false;
    const source = visitedPartSource(docs, (partDocId, partDoc) => {
        if (!partDoc) {
            absentPartDocIds.push(partDocId);
            return;
        }
        partChanging ||= isChanging(partDoc);
        parts.push({ partDocId, doc: partDoc, epoch: transactionEpoch(partDoc) });
    });
    const value = read(stored, source);
    if (keep(value) && !partChanging)
        cache.set(stored, { epoch, parts, absentPartDocIds, value });
    return value;
}
const directoryReads = new WeakMap();
/**
 * Complete directory of a database block model or snapshot, if the block has
 * one. Throws {@link DatabaseShardDirectoryPartsMissingError} while a part
 * document is not loaded; readers that can wait use
 * {@link readDatabaseShardDirectoryState}. Cell reads ask for it many times per
 * row, so a live model reads it once per change.
 */
function readDatabaseShardDirectory(host) {
    return readCachedDirectory(directoryReads, host, (stored, source) => completeDirectory(stored, readStoredDirectory(stored, source)), directory => directory !== undefined);
}
const headReads = new WeakMap();
const loadedReads = new WeakMap();
/**
 * Head of the directory of a database block model as stored: database fields,
 * the row counts of the closed parts and the open last part by id, without
 * reading a part. The numbers of the open part come with
 * {@link readDatabaseShardDirectoryLoaded} (`head`).
 */
function readDatabaseShardDirectoryHead(host) {
    return readCachedDirectory(headReads, host, stored => freezeHead(headOfStored(stored)), head => head !== undefined);
}
/**
 * Directory part documents of a database block model, without reading a
 * part. None for a block without a directory.
 */
function readDatabaseShardDirectoryPartDocIds(host) {
    return partDocIdsOfStored(storedDatabaseShardDirectory(propsOf(host)));
}
/**
 * Directory part documents holding the shards `shardDocIds` among the loaded
 * parts of a database block model: the parts a row write of these shards
 * reads.
 */
function readDatabaseShardDirectoryShardPartDocIds(host, shardDocIds) {
    const loaded = readDatabaseShardDirectoryLoaded(host);
    if (!loaded)
        return [];
    const wanted = new Set(shardDocIds);
    return loaded.loadedParts
        .filter(part => part.shards.some(shard => wanted.has(shard.serverDocId)))
        .map(part => part.partDocId);
}
function storedBlockDirectory(block) {
    return (0, database_manifest_fields_1.readManifestField)(block, database_shard_manifest_crdt_1.DATABASE_SHARD_MANIFEST_PROP);
}
/**
 * Head of the directory stored in a database block map or its JSON form, as
 * stored: database fields, the row counts of the closed parts and the open
 * last part by id, without reading a part
 * ({@link resolveDatabaseShardDirectoryHead} adds the open part's numbers).
 */
function readDatabaseShardDirectoryBlockHead(block) {
    return headOfStored(storedBlockDirectory(block));
}
/**
 * Part documents of the directory stored in a database block map or its JSON
 * form, for readers of document bytes that load them before
 * {@link readDatabaseShardDirectoryBlockState}. None for a block without a
 * directory.
 */
function readDatabaseShardDirectoryBlockPartDocIds(block) {
    return partDocIdsOfStored(storedBlockDirectory(block));
}
/**
 * Complete directory stored in a database block map or its JSON form, with
 * its parts read from `source`. Throws
 * {@link DatabaseShardDirectoryPartsMissingError} while a part is missing;
 * readers that can wait use {@link readDatabaseShardDirectoryBlockState}.
 */
function readDatabaseShardDirectoryBlock(block, source) {
    const stored = storedBlockDirectory(block);
    return completeDirectory(stored, readStoredDirectory(stored, source));
}
/**
 * Complete directory stored in a database block map or its JSON form, with
 * its parts read from `source`, or the part documents still to load. For
 * readers of document bytes that load the parts themselves; block models use
 * {@link readDatabaseShardDirectoryState}.
 */
function readDatabaseShardDirectoryBlockState(block, source) {
    return readStoredDirectory(storedBlockDirectory(block), source);
}
/**
 * Directory parts the head stored in a database block map or its JSON form
 * names, with their documents, without reading a part. None for a block
 * without a directory.
 */
function readDatabaseShardDirectoryBlockParts(block) {
    const head = headOfStored(storedBlockDirectory(block));
    return head
        ? head.parts.map(part => ({
            partId: part.partId,
            partDocId: (0, database_shard_directory_parts_1.databaseShardDirectoryPartDocId)(head, part.partId),
        }))
        : [];
}
/**
 * Complete directory stored in a database block map or its JSON form, with
 * every part document it names loaded through `loadPart`. For readers of
 * stored documents, which have no workspace to wait on: a part the store does
 * not hold, or not in step with the head, throws
 * {@link DatabaseShardDirectoryPartsMissingError}.
 */
async function loadDatabaseShardDirectoryBlock(block, loadPart) {
    const loaded = new Map(await Promise.all(readDatabaseShardDirectoryBlockPartDocIds(block).map(async (partDocId) => [partDocId, await loadPart(partDocId)])));
    return readDatabaseShardDirectoryBlock(block, partDocId => loaded.get(partDocId));
}
/**
 * Shards holding the rows `[offset, offset + limit)` of a database block
 * model, with the rows before the first of them, or the part documents still
 * to load. Rendering asks for it per window, so it reads no part: it serves
 * the window from the head and the loaded directory.
 */
function readDatabaseShardDirectoryWindow(host, request) {
    const loaded = readDatabaseShardDirectoryLoaded(host);
    return loaded && (0, database_shard_directory_parts_1.readDatabaseShardDirectoryLoadedWindow)(loaded, request);
}
/**
 * Directory of a database block model or snapshot as far as its parts are
 * loaded from the first part on, with the parts still to load.
 */
function readDatabaseShardDirectoryLoaded(host) {
    return readCachedDirectory(loadedReads, host, loadedOfStored, loaded => loaded !== undefined);
}
function loadedOfStored(stored, source) {
    const head = headOfStored(stored);
    if (!head)
        return undefined;
    const loaded = (0, database_shard_directory_parts_1.readDatabaseShardDirectoryHeadLoaded)(head, source);
    freezeDirectory(loaded.manifest);
    freezeDirectory(loaded.loadedManifest);
    for (const part of loaded.loadedParts)
        Object.freeze(part);
    Object.freeze(loaded.loadedParts);
    Object.freeze(loaded.missingParts);
    return Object.freeze(loaded);
}
/**
 * Documents to observe for the rows of a database: its directory part
 * documents and the row shards of the loaded leading parts. Observers read it
 * again when a part document changes, so the shards of a part that arrives
 * later join them.
 */
function readDatabaseShardDirectoryDocIds(host) {
    const loaded = readDatabaseShardDirectoryLoaded(host);
    if (!loaded)
        return undefined;
    return {
        partDocIds: readDatabaseShardDirectoryPartDocIds(host),
        shardDocIds: loaded.manifest.shards.map(shard => shard.serverDocId),
    };
}
/**
 * Directory stored in block props or a block identity record. Parts are read
 * from `docs`; without them a stored head throws
 * {@link DatabaseShardDirectoryPartsMissingError}.
 */
function readDatabaseShardDirectoryProps(props, docs) {
    const stored = storedDatabaseShardDirectory(props);
    return completeDirectory(stored, readStoredDirectory(stored, databaseShardDirectoryPartSource(docs)));
}
/** Whether a database block model or snapshot stores a directory. */
function hasDatabaseShardDirectory(host) {
    return storedDatabaseShardDirectory(propsOf(host)) !== undefined;
}
/** Whether a JSON block snapshot carries a directory. */
function snapshotPropsHaveDatabaseShardDirectory(props) {
    return storedDatabaseShardDirectory(props) !== undefined;
}
/**
 * Block snapshot props without the directory they carry: a block restored
 * from a snapshot is a new block, and its directory belongs to the database
 * it was taken from. Provisioning, copies and clones store its own.
 */
function withoutDatabaseShardDirectory(props) {
    const { [DIRECTORY_PROP_KEY]: _carried, ...rest } = props;
    return rest;
}
/**
 * Subscribes the running signal computation to a replacement of the
 * directory prop. Nested CRDT changes do not replace the prop, so readers
 * pair this with their own revision signal and read the directory itself
 * with {@link readDatabaseShardDirectory}.
 */
function trackDatabaseShardDirectory(host) {
    void host.props.databaseShardManifest$.value;
}
/** Whether a changed prop key of a database block is the directory. */
function isDatabaseShardDirectoryProp(key) {
    return key === DIRECTORY_PROP_KEY || key === database_shard_manifest_crdt_1.DATABASE_SHARD_MANIFEST_PROP;
}
/** Block props carrying `manifest` in a JSON block snapshot. */
function databaseShardDirectorySnapshotProps(manifest) {
    return {
        [DIRECTORY_PROP_KEY]: {
            ...manifest,
            shards: manifest.shards.map(({ rowFingerprints, ...shard }) => rowFingerprints
                ? {
                    ...shard,
                    rowFingerprints: Object.fromEntries(Object.entries(rowFingerprints).map(([session, bytes]) => [
                        session,
                        (0, database_bytes_text_1.encodeDatabaseBytesText)(bytes),
                    ])),
                }
                : shard),
        },
    };
}
function snapshotShardEntry(shard) {
    const texts = (0, database_manifest_fields_1.readManifestField)(shard, 'rowFingerprints');
    if (texts === undefined)
        return shard;
    if (!texts || typeof texts !== 'object' || Array.isArray(texts))
        throw new database_storage_errors_1.DatabaseShardDirectoryFormatError(`Database row shard ${(0, database_manifest_fields_1.readStringField)(shard, 'shardId')} has no row fingerprint record in its snapshot`);
    return {
        ...shard,
        rowFingerprints: Object.fromEntries(Object.entries(texts).map(([session, text]) => {
            if (!(0, database_bytes_text_1.isDatabaseBytesText)(text))
                throw new database_storage_errors_1.DatabaseShardDirectoryFormatError(`Database row shard ${(0, database_manifest_fields_1.readStringField)(shard, 'shardId')} has a row fingerprint that is not base64 text`);
            return [session, (0, database_bytes_text_1.decodeDatabaseBytesText)(text)];
        })),
    };
}
/**
 * The complete directory a JSON block snapshot carries
 * ({@link databaseShardDirectorySnapshotProps}). A head there is no snapshot
 * form and throws {@link DatabaseShardDirectoryFormatError}.
 */
function readDatabaseShardDirectoryBlockSnapshot(props) {
    const carried = storedDatabaseShardDirectory(props);
    if (carried === undefined)
        return undefined;
    if ((0, database_shard_directory_parts_1.isDatabaseShardDirectoryHead)(carried))
        throw new database_storage_errors_1.DatabaseShardDirectoryFormatError('A database block snapshot carries the complete directory, not a head');
    const shards = (0, database_manifest_fields_1.readManifestField)(carried, 'shards');
    return (0, index_1.normalizeDatabaseShardManifest)(Array.isArray(shards)
        ? {
            ...carried,
            shards: shards.map(snapshotShardEntry),
        }
        : carried);
}
/** Whether a database block map stores a directory. */
function blockHasDatabaseShardDirectory(block) {
    return block.has(database_shard_manifest_crdt_1.DATABASE_SHARD_MANIFEST_PROP);
}
/**
 * The stored CRDT map of the directory of a database block map, for writers
 * that wrap it in a reactive proxy. Read its contents only through the head
 * readers here and write them only through the change plan of
 * `database-shard-directory-write`.
 */
function databaseShardDirectoryMap(block) {
    return block.get(database_shard_manifest_crdt_1.DATABASE_SHARD_MANIFEST_PROP);
}
/** Removes the directory a database block map stores. */
function deleteDatabaseShardDirectory(block) {
    block.delete(database_shard_manifest_crdt_1.DATABASE_SHARD_MANIFEST_PROP);
}
/**
 * Field of the directory in a database block map, for tree readers that fetch
 * it together with other block fields; they read the fetched value with
 * `normalizeDatabaseShardDirectoryHead`.
 */
exports.DATABASE_SHARD_DIRECTORY_FIELD = database_shard_manifest_crdt_1.DATABASE_SHARD_MANIFEST_PROP;
/** Path of the directory below a block in a plain or CRDT doc tree. */
function databaseShardDirectoryPath(blockId) {
    return [blockId, database_shard_manifest_crdt_1.DATABASE_SHARD_MANIFEST_PROP];
}
